import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { articlesApi, type ArticleDetail, type ArticleView } from "../api/articles";
import Ic from "./icons.jsx";

/** Picked article — what a sastavnica line needs to show before the server answers. */
export type ArticleRef = { id: string; code: string; name: string; unit: string; typeName: string };

export function articleRef(a: ArticleView | ArticleDetail): ArticleRef {
  return { id: a.id, code: a.code, name: a.name, unit: a.unit, typeName: a.typeName };
}

const PANEL_GAP = 4;
const PANEL_MAX_HEIGHT = 300;
const SEARCH_DEBOUNCE_MS = 250;
const PAGE = 20;

/**
 * Input + first 20 matches from `GET /articles?q=` (code, name and type name, server-side,
 * diacritics-insensitive); Escape / outside click closes. Used by the sastavnica line modal.
 * Same mechanics as `AssetTypeCombobox`: the list is a fixed portal anchored to the input
 * (a scrolling modal never clips it, it flips upwards when there is no room below). With
 * `onCreateNew` the list ends with a "Nov artikal" row — the caller gates it with
 * `articles:create`.
 */
export function ArticleCombobox({
  value, excludeId, onChange, disabled, placeholder, onCreateNew, autoFocus,
}: {
  value: ArticleRef | null;
  /** Hidden from the results. */
  excludeId?: string;
  onChange: (v: ArticleRef | null) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Renders the "Nov artikal" row at the end of the list; absent = no row. */
  onCreateNew?: () => void;
  autoFocus?: boolean;
}) {
  const { t } = useTranslation();
  const listId = useId();
  const [query, setQuery] = useState(value ? `${value.code} — ${value.name}` : "");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<ArticleView[]>([]);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<CSSProperties | null>(null);

  // A value picked elsewhere (the article created inline) shows up in the box.
  useEffect(() => {
    if (value) setQuery(`${value.code} — ${value.name}`);
  }, [value]);

  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    function place() {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const viewportH = window.innerHeight;
      const below = viewportH - r.bottom - PANEL_GAP;
      const above = r.top - PANEL_GAP;
      const wanted = Math.min(PANEL_MAX_HEIGHT, (Math.max(results.length, 1) + (onCreateNew ? 1 : 0)) * 44 + 8);
      const flip = below < wanted && above > below;
      const maxHeight = Math.max(120, Math.min(PANEL_MAX_HEIGHT, flip ? above : below));
      setPos(flip
        ? { position: "fixed", left: r.left, width: r.width, bottom: viewportH - r.top + PANEL_GAP, maxHeight }
        : { position: "fixed", left: r.left, width: r.width, top: r.bottom + PANEL_GAP, maxHeight });
    }
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, results.length, onCreateNew]);

  useEffect(() => {
    if (!open) return;
    const h = setTimeout(() => {
      setLoading(true);
      articlesApi
        .list({ q: query.trim() || undefined, size: PAGE, sort: "code,asc" })
        .then((res) => setResults(res.content.filter((x) => x.id !== excludeId)))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(h);
  }, [query, open, excludeId]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const target = e.target as Node;
      if (wrapRef.current?.contains(target) || listRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  function pick(x: ArticleView) {
    onChange(articleRef(x));
    setQuery(`${x.code} — ${x.name}`);
    setOpen(false);
  }

  function clear() {
    onChange(null);
    setQuery("");
    setResults([]);
  }

  return (
    <div ref={wrapRef} className="combo">
      <div style={{ display: "flex", gap: 6 }}>
        <input
          className="input"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listId}
          aria-label={t("bom.field.article", "Artikal")}
          value={query}
          disabled={disabled}
          autoFocus={autoFocus}
          placeholder={placeholder ?? t("articles.combo.placeholder", "Pretraži artikle po šifri, nazivu ili tipu…")}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActive(0);
            if (value) onChange(null);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((a) => Math.min(results.length - 1, a + 1)); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
            else if (e.key === "Enter" && open && results[active]) { e.preventDefault(); pick(results[active]); }
            else if (e.key === "Escape") setOpen(false);
          }}
        />
        {value && !disabled && (
          <button type="button" className="btn" onClick={clear} aria-label={t("action.clear", "Očisti")} title={t("action.clear", "Očisti")}>
            <Ic.x />
          </button>
        )}
      </div>
      {open && !disabled && pos && createPortal(
        <div id={listId} ref={listRef} role="listbox" className="combo-list combo-list-portal dd-scroll" style={pos}>
          {loading && results.length === 0 ? (
            <div className="combo-empty">{t("common.loading", "Učitavanje…")}</div>
          ) : results.length === 0 ? (
            <div className="combo-empty">{t("articles.combo.empty", "Nema artikala koji odgovaraju pretrazi.")}</div>
          ) : (
            results.map((x, i) => (
              <button
                key={x.id}
                type="button"
                role="option"
                aria-selected={value?.id === x.id}
                className={`combo-opt${i === active ? " is-active" : ""}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(x)}
              >
                <span className="code">{x.code}</span>
                <span style={{ flex: 1, minWidth: 0 }}>{x.name}</span>
                <span className="combo-type">{x.typeName}</span>
              </button>
            ))
          )}
          {onCreateNew && (
            <button type="button" className="combo-opt combo-opt-new" onClick={() => { setOpen(false); onCreateNew(); }}>
              <Ic.plus /> {t("articles.combo.new", "Nov artikal")}
            </button>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
